import { InitialResolver } from "@/lib/use.middlewares";
import Image from "next/image";
import styles from "./index.module.scss";
import Link from "next/link";
import { RNPL_PROVIDERS, RNPL_SERVICES_LINK } from "@/common/constants";
import React, { useMemo, useState } from "react";
import { financialFormat } from "@/lib/helpers";
import { FIND_LISTINGS_WITH_SOV } from "@/graphql/queries/find.listings";
import { GET_ALL_CITIES } from "@/graphql/queries/regions";
import { dehydrateApolloClient, initApolloClient } from "@/lib/apolloClient";
import { usePath } from "@/contexts/path/path";
import CitiesList from "@/components/harajRnpl/CitiesList";
import { useAppState } from "@/contexts/app/app.provider";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { useRouter } from "next/router";
import { HarajRizeForm } from "@/components/rnpl/HarajRizeForm";
import { HarajEjariForm } from "@/components/rnpl/HarajEjariForm";
import SuggestedListings from "@/components/harajRnpl/SuggestedListings";
import { sharedHarajRNPLTracker } from "@/lib/tracking/trackHarajRNPLEvents";

const HarajRNPLPage = () => {
  const { isMobile } = useAppState();
  const { token } = useAuthState();
  const { harajRNPLData, previousPath } = usePath();
  const router = useRouter();
  const [selectedProvider, setSelectedProvider] = useState(null);

  const showRizeFirst = useMemo(() => {
    const date = new Date();
    const hourSeed = date.getHours();
    const minuteSeed = date.getMinutes();
    return (hourSeed * 60 + minuteSeed) % 2 === 0;
  }, []);

  const category = harajRNPLData?.fullCategory;

  const handleProviderSelection = (provider) => {
    if (!token) {
      return router.push(
        `/login?returnTo=/haraj/rnpl/${router.query.haraj_listing_id}`,
      );
    }
    sharedHarajRNPLTracker({
      target: `${provider.toLowerCase()}_initiate`,
      source: previousPath || encodeURI(document.referrer),
    });
    setSelectedProvider(provider);
  };

  return (
    <div className={styles.harajRnpl}>
      <div className={styles.container}>
        <h1 className={styles.pageTitle}>الدفعات الشهرية</h1>
        <div className={styles.card}>
          <div className={styles.imageContainer}>
            <Image src="/haraj_rnpl_banner.png" alt="haraj banner" fill />
          </div>

          <div className={styles.description}>
            <p className={styles.descriptionText}>
              {`خدمة مقدمة من عقار و حراج بالتعاون مع الشركاء ${
                showRizeFirst ? "(رايز - إيجاري)" : "(إيجاري - رايز)"
              } لتقسيط الإيجار السنوي على دفعات شهرية`}
            </p>
            <Link
              href={RNPL_SERVICES_LINK.harajTenent}
              className={styles.link}
              target="_blank"
              onClick={() => {
                sharedHarajRNPLTracker({
                  target: "learn_more_about_the_service",
                  source: previousPath || encodeURI(document.referrer),
                });
              }}
            >
              اعرف المزيد عن الخدمة
            </Link>
          </div>

          <div className={styles.details}>
            <h3 className={styles.detailsTiles}>العقار المُختار</h3>
            <div className={styles.detailsTable}>
              <div className={styles.row}>
                <div className={styles.label}>نوع العقار</div>
                <div className={styles.value}>
                  {harajRNPLData?.fullCategory?.name}
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.label}>الإيجار السنوي</div>
                <div className={`${styles.value} ${styles.green}`}>
                  {financialFormat(harajRNPLData?.price, "ريال")}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.providersSection}>
            <h4 className={styles.text}>الرجاء اختيار مزود الخدمة</h4>
            <div
              className={styles.providersList}
              style={{ flexDirection: showRizeFirst ? "row" : "row-reverse" }}
            >
              <button
                className={`${styles.providerButton} ${
                  selectedProvider === RNPL_PROVIDERS.rize.name
                    ? styles.active
                    : ""
                }`}
                onClick={() =>
                  handleProviderSelection(RNPL_PROVIDERS.rize.name)
                }
              >
                <Image src="/rize.png" alt="rize logo" width={50} height={18} />
                <div className={styles.buttonText}>
                  تبدأ من{" "}
                  {financialFormat(harajRNPLData?.monthly_price, "ريال")} /
                  شهريًا
                </div>
              </button>
              <button
                className={`${styles.providerButton} ${
                  selectedProvider === RNPL_PROVIDERS.ejari.name
                    ? styles.active
                    : ""
                }`}
                onClick={() =>
                  handleProviderSelection(RNPL_PROVIDERS.ejari.name)
                }
              >
                <Image
                  src="/ejari.png"
                  alt="ejari logo"
                  width={50}
                  height={20}
                />
                <div className={styles.buttonText}>
                  تبدأ من{" "}
                  {financialFormat(harajRNPLData?.monthly_price, "ريال")} /
                  شهريًا
                </div>
              </button>
            </div>

            {selectedProvider === RNPL_PROVIDERS.rize.name && (
              <HarajRizeForm
                adLicenseNumber={harajRNPLData?.ad_license_number}
              />
            )}

            {selectedProvider === RNPL_PROVIDERS.ejari.name && (
              <HarajEjariForm
                adLicenseNumber={harajRNPLData?.ad_license_number}
                handleProviderSelection={handleProviderSelection}
              />
            )}
          </div>
        </div>

        <div className={styles.citiesList}>
          <CitiesList
            category={category}
            numberOfCities={4}
            isMobile={isMobile}
            trackingEvent={() => {
              sharedHarajRNPLTracker({
                target: "list_cities",
                source: previousPath || encodeURI(document.referrer),
                position: "top",
              });
            }}
          />
        </div>

        <div className={styles.suggestedListWrapper}>
          <SuggestedListings
            categoryId={category?.id}
            cityId={harajRNPLData?.city_id}
          />
        </div>

        <div className={styles.citiesList}>
          <CitiesList
            category={category}
            numberOfCities={isMobile ? 4 : 8}
            isMobile={isMobile}
            trackingEvent={() => {
              sharedHarajRNPLTracker({
                target: "list_cities",
                source: previousPath || encodeURI(document.referrer),
                position: "bottom",
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

HarajRNPLPage.getInitialProps = InitialResolver(async (ctx, $res) => {
  const { categories } = await import(
    "../../../../lib/path-resolver-middlewares/ssr/categories"
  );

  const client = initApolloClient();

  const defaultCity = 21; // Riyadh
  const { haraj_listing_id } = ctx.query;
  const res =
    typeof window === "undefined"
      ? await fetch(`${process.env.HARAJ_RNPL_EP}${haraj_listing_id}`, {
          method: "GET",
          headers: {
            "x-haraj-auth-token": `${process.env.HARAJ_RNPL_AUTH_TOKEN}`,
          },
        })
      : await fetch(`/next-api/haraj-listing/${haraj_listing_id}`);
  const data = await res.json();
  if (!data?.success) {
    ctx.redirect("/404");
  }

  const category = data?.data?.category_id;
  const city = data?.data?.city_id || defaultCity;
  const fullCategory = Object.values(categories).find(
    ({ id }) => id === category,
  );
  ctx.path.harajRNPLData = {
    ...data.data,
    fullCategory,
  };

  await Promise.all([
    client.query({
      query: FIND_LISTINGS_WITH_SOV,
      variables: {
        size: 20,
        from: 0,
        sort: {
          create_time: "desc",
          has_img: "desc",
        },
        where: {
          category: {
            eq: category,
          },
          city_id: {
            eq: city,
          },
        },
        sov: {
          listing_category: category,
          city_id: city,
          enabled: true,
          campaign_category: "PROMOTED",
        },
      },
    }),
    client.query({
      query: GET_ALL_CITIES,
      variables: {
        category,
      },
    }),
  ]);

  return $res(dehydrateApolloClient(client, {}));
});
export default HarajRNPLPage;
